<template>
  <div>
    <page-content>
      <form @submit.prevent="saveIntro">
        <div class="px-4 py-3 bg-white mb-3">
          <formly-form :form="introForm" :model="introModel" :fields="introFields"></formly-form>
          <button class="btn btn-success push-15">{{ $t('save') }}</button>
        </div>
      </form>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <h4 class="mb-0">{{ $t('aboutUs.pages') }}</h4>
        <button type="button" class="btn btn-success btn-sm" @click="addPage">
          <i class="fa fa-plus mr-1"></i>{{ $t('aboutUs.addPage') }}
        </button>
      </div>

      <p v-if="!pages.length" class="text-muted">{{ $t('aboutUs.noPages') }}</p>

      <draggable
        handle=".drag-handle"
        v-model="pages"
        v-bind="dragOptions"
        @start="drag = true"
        @end="onDragEnd"
      >
        <transition-group type="transition" :name="!drag ? 'flip-list' : null">
          <div class="block block-rounded mb-3" v-for="page in pages" :key="page._key">
            <div class="block-header block-header-default" @click="toggleCollapse(page, $event)">
              <h3 class="block-title">
                <i class="fa fa-bars drag-handle mr-3" style="cursor: grab;"></i>
                {{ page.title || $t('aboutUs.newPage') }}
              </h3>
              <div class="block-options">
                <button type="button" class="btn btn-sm btn-danger" @click.stop="deletePage(page)">
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>
            <div class="block-content bg-white pb-3" v-show="!isCollapsed(page._key)">
              <div class="form-group">
                <label class="font-w600">{{ $t('formly.fields.title') }}</label>
                <input v-model="page.title" type="text" class="form-control" />
              </div>
              <div class="form-group">
                <label class="font-w600">{{ $t('formly.fields.text') }}</label>
                <vue-editor
                  v-model="page.text"
                  useCustomImageHandler
                  :editor-toolbar="editorToolbar"
                  :editorOptions="editorOptions"
                  @image-added="(file, editor, cursor, reset) => handleImageAdded(file, editor, cursor, reset)"
                ></vue-editor>
              </div>
              <div class="d-flex justify-content-end">
                <button type="button" class="btn btn-success btn-sm" @click="savePage(page)">
                  <i class="fa fa-save mr-1"></i>{{ $t('save') }}
                </button>
              </div>
            </div>
          </div>
        </transition-group>
      </draggable>
    </page-content>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import SettingsService from '@/services/settings.service'
import MediaService from '@/services/media.service'
import { VueEditor, Quill } from 'vue2-editor'
import { ImageDrop } from 'quill-image-drop-module'
import ImageResize from 'quill-image-resize-module'
import htmlEditButton from 'quill-html-edit-button'

Quill.register({
  'modules/imageDrop': ImageDrop,
  'modules/imageResize': ImageResize,
  'modules/htmlEditButton': htmlEditButton
})

let _keyCounter = 0

export default {
  components: { VueEditor, draggable },
  data () {
    return {
      introModel: {},
      introForm: {},
      introFields: [
        {
          key: 'aboutUs',
          type: 'html',
          templateOptions: { label: 'aboutUs' }
        }
      ],
      pages: [],
      collapseStates: {},
      drag: false,
      dragOptions: {
        animation: 200,
        group: 'about-us-pages',
        disabled: false,
        ghostClass: 'ghost'
      },
      editorToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
        ['blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ color: [] }, { background: [] }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      editorOptions: {
        modules: {
          imageDrop: true,
          imageResize: {},
          htmlEditButton: {
            prependSelector: '#main-container',
            buttonHTML: '<strong><i class="fa fa-code" aria-hidden="true"></i></strong>'
          }
        }
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    Promise.all([
      SettingsService.index(),
      SettingsService.indexAboutUsPages()
    ]).then(([settingsRes, pagesRes]) => {
      next(vm => vm.setData(settingsRes.data, pagesRes.data))
    })
  },
  methods: {
    setData (settings, pages) {
      this.introModel = { aboutUs: settings.aboutUs }
      this.pages = pages.map(p => {
        const key = ++_keyCounter
        this.$set(this.collapseStates, key, true)
        return { ...p, _key: key }
      })
    },
    saveIntro () {
      SettingsService.storeBrandingWithoutLoading(this.introModel).then(() => {
        this.introForm.$errors = {}
      }).catch(err => {
        this.introForm.$errors = err.response?.data?.errors || {}
      })
    },
    addPage () {
      const key = ++_keyCounter
      this.$set(this.collapseStates, key, false)
      this.pages.push({ _key: key, uuid: null, title: '', text: '' })
    },
    savePage (page) {
      const payload = { title: page.title, text: page.text }
      if (page.uuid) {
        SettingsService.updateAboutUsPage(page.uuid, payload)
      } else {
        SettingsService.storeAboutUsPage(payload).then(() => {
          this.fetchPages()
        })
      }
    },
    fetchPages () {
      SettingsService.indexAboutUsPages().then(res => {
        const existing = Object.fromEntries(this.pages.filter(p => p.uuid).map(p => [p.uuid, p._key]))
        this.pages = res.data.map(p => {
          const key = existing[p.uuid] ?? ++_keyCounter
          if (this.collapseStates[key] === undefined) {
            this.$set(this.collapseStates, key, true)
          }
          return { ...p, _key: key }
        })
      })
    },
    deletePage (page) {
      if (!page.uuid) {
        this.pages = this.pages.filter(p => p._key !== page._key)
        return
      }
      SettingsService.deleteAboutUsPage(page.uuid).then(() => {
        this.pages = this.pages.filter(p => p._key !== page._key)
      })
    },
    onDragEnd () {
      this.drag = false
      SettingsService.changeOrderAboutUsPages({
        pages: this.pages.filter(p => p.uuid).map(p => ({ uuid: p.uuid }))
      })
    },
    isCollapsed (key) {
      return !!this.collapseStates[key]
    },
    toggleCollapse (page, event) {
      if (this.drag) return
      if (event && (event.target.closest('.drag-handle') || event.target.closest('.block-options'))) return
      this.$set(this.collapseStates, page._key, !this.isCollapsed(page._key))
    },
    handleImageAdded (file, editor, cursorLocation, resetUploader) {
      const formData = new FormData()
      formData.append('file', file)
      MediaService.uploadAttachment(formData).then(res => {
        editor.insertEmbed(cursorLocation, 'image', res.data.url)
        resetUploader()
      })
    }
  }
}
</script>

<style scoped>
.flip-list-move {
  transition: transform 0.5s;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
</style>
